import React, { memo, useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { ERequestStatus } from '@r1-frontend/api-domru/api-request/v1/user/request-by-connection';
import { checkAddress } from '@r1-frontend/entities/ContactAddress/actions/checkAddress';
import { fillAddressAction } from '@r1-frontend/entities/ContactAddress/actions/fillAddress';
import { setAddressEdit } from '@r1-frontend/entities/ContactAddress/actions/setAddressEdit';
import { isAddressFilled } from '@r1-frontend/entities/ContactAddress/selectors/isAddressFilled';
import { EVerificationStatus } from '@r1-frontend/entities/ContactDetail/initialState';

import { IAddress } from '@r1-frontend/shared/types/IAddress';

import { TState } from '~/src/store';

import { ConfirmedForm } from './ConfirmedForm';
import EditableForm from './EditableForm';
import { useStorageAddress } from './useStorageAddress';

import * as ST from './styled';

export type TField = {
    value: string,
    isValid: boolean,
    clearInput: () => void,
}

export interface IResp {
    ok: boolean,
    message: string,
    status: ERequestStatus,
    id: number | undefined,
}

interface IProps {
    showConfirmButton?: boolean,
    showConfirmForm?: boolean,
    addressBlockRef?: React.RefObject<HTMLDivElement>,
}

const ContactAddress = ({ showConfirmButton = true, showConfirmForm = true, addressBlockRef }: IProps): JSX.Element => {
    const dispatch = useDispatch();

    const {
        isConfirmButtonActive,
        verifiedAddress,
        addressStatus,
        isEdit,
    } = useSelector((state: TState) => ({
        isConfirmButtonActive: isAddressFilled(state),
        verifiedAddress: state.contactAddress.verifiedAddressData.address,
        addressStatus: state.contactAddress.verifiedAddressData.status,
        editedAddress: state.contactAddress.editedAddressData,
        isEdit: state.contactAddress.isEdit,
    }), shallowEqual);

    useStorageAddress();

    const onEdit = useCallback(() => {
        dispatch(setAddressEdit(true));
    }, [dispatch]);

    const onFillAddress = useCallback((address: IAddress) => {
        dispatch(fillAddressAction(address));
    }, [dispatch]);

    const checkAddressData = () => {
        dispatch(checkAddress());
        dispatch(setAddressEdit(false));
    };

    return (
        <ST.AddressWrapper ref={addressBlockRef} className={'ym-hide-content'}>
            {isEdit &&
                <EditableForm
                    isConfirmButtonActive={isConfirmButtonActive}
                    defaultAddress={verifiedAddress}
                    showConfirmButton={showConfirmButton}
                    withSatelliteFilter={false}
                    onFillAddress={onFillAddress}
                    onConfirmBtnClick={checkAddressData}
                />
            }

            {!isEdit && showConfirmForm &&
                <ConfirmedForm
                    isDisableBtn={addressStatus === EVerificationStatus.verifying}
                    onEdit={onEdit}
                />
            }
            {(addressStatus === EVerificationStatus.verifying) &&
                <ST.STrobber/>
            }
        </ST.AddressWrapper>
    );
};

export default memo(ContactAddress);
